import { Box } from '@material-ui/core';
import { DocumentationRequired, DocumentationRequiredType, Loan } from '@nemuru-eng/js-api-client';

import { DocumentationRequiredProgressBar } from '@/components/modules/Documentation/components/shared/DocumentationRequiredProgressBar';
import { useDocumentationRequired } from '@/components/modules/Documentation/hooks/useDocumentationRequired';
import { useParticipantParamsByType } from '@/components/modules/Documentation/hooks/useParticipantParamsByType';
import { DocumentStatusWithSent } from '@/components/modules/Documentation/types/DocumentStatusWithSent';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { ErrorIcon } from '@/components/shared/components/ErrorIcon/ErrorIcon';
import { Font } from '@/components/shared/components/Font';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface DocumentationRequiredCardProps {
  currentLoan: Loan;
  documentationRequiredType: DocumentationRequiredType;
  documentationRequired: DocumentationRequired;
  selectedDocumentationRequired: DocumentationRequired;
  selectDocumentationRequired: (documentationRequired: DocumentationRequired) => void;
}

export const DocumentationRequiredCard = ({
  currentLoan,
  documentationRequiredType,
  documentationRequired,
  selectedDocumentationRequired,
  selectDocumentationRequired,
}: DocumentationRequiredCardProps) => {
  const { palette } = useCustomTheme();

  const { getParticipantCardParamsByType } = useParticipantParamsByType();

  const { uploadedProgress, status } = useDocumentationRequired({ documentationRequired, loanId: currentLoan.id });

  const documentationCardParams = getParticipantCardParamsByType({
    currentLoan,
    documentationRequiredType,
    documentationRequired,
  });

  const isSelected = selectedDocumentationRequired.id === documentationRequired.id;

  if (!documentationCardParams) {
    return null;
  }

  return (
    <CustomPaper
      fill={isSelected ? 'secondary.dark' : 'paper'}
      onClick={() => selectDocumentationRequired(documentationRequired)}
      style={{
        cursor: 'pointer',
        padding: '6px 16px 6px 4px',
      }}
    >
      <Box height={'40px'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'} width={'70%'}>
          <Box pr={1}>
            <SizedIconWithCircle
              variant={'medium'}
              backgroundColor={'transparent'}
              iconColor={isSelected ? palette.secondary.contrastText : palette.secondary.main}
              customBorderRadius={0}
              iconName={documentationCardParams.iconName}
            />
          </Box>
          <Box textAlign={'left'} className={'overflow-text-ellipsis'}>
            <Box>
              <Font
                variant="overline"
                style={{
                  color: isSelected ? palette.secondary.contrastText : palette.text.secondary,
                }}
              >
                {documentationCardParams.labelText}
              </Font>
            </Box>
            <Font
              variant="body2"
              style={{
                color: isSelected ? palette.secondary.contrastText : palette.text.title,
              }}
            >
              <strong>{documentationCardParams.labelIdentifier}</strong>
            </Font>
          </Box>
        </Box>
        <Box width={'30%'}>
          <Box display={'flex'} alignItems={'center'} width={'100%'}>
            <Box width={'20px'} display={'flex'} justifyContent={'center'} mx={1}>
              {status === DocumentStatusWithSent.INCIDENCE && (
                <ErrorIcon errorDetected={true} isDocumentationWithIncidence={true} />
              )}
              {status !== DocumentStatusWithSent.INCIDENCE && uploadedProgress >= 100 && (
                <i
                  className="material-icons-outlined"
                  style={{
                    color: isSelected ? palette.secondary.contrastText : palette.secondary.main,
                    fontSize: '16px',
                  }}
                >
                  check
                </i>
              )}
            </Box>
            <Box flexGrow={1}>
              <DocumentationRequiredProgressBar uploadedProgress={uploadedProgress} selected={isSelected} />
            </Box>
          </Box>
        </Box>
      </Box>
    </CustomPaper>
  );
};
